/**
 * HeadJudgeLogPage - Real-time task/incident log for head judges.
 *
 * Used by head judges (päätuomarit) to communicate and track issues
 * during the tournament day.
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { headJudgeLogApi, fieldsApi } from '@/api/client';
import type { HeadJudgeLogCreateRequest, HeadJudgeLogUpdateRequest } from '@/api/client';
import type { HeadJudgeLogEntry, HeadJudgeLogStatus, AdminUser, Field } from '@/types';

// Status configuration
const STATUS_CONFIG: Record<HeadJudgeLogStatus, { label: string; color: string; bgColor: string }> = {
  new: { label: 'Uusi', color: '#fff', bgColor: '#d32f2f' },
  in_progress: { label: 'Hoidossa', color: '#000', bgColor: '#ff9800' },
  done: { label: 'Hoidettu', color: '#fff', bgColor: '#388e3c' },
};

const STATUS_OPTIONS: HeadJudgeLogStatus[] = ['new', 'in_progress', 'done'];

// Auto-refresh interval (10 seconds)
const REFRESH_INTERVAL = 10000;

export function HeadJudgeLogPage() {
  const queryClient = useQueryClient();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<HeadJudgeLogStatus | 'all'>('all');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formDescription, setFormDescription] = useState('');
  const [formField, setFormField] = useState<Field | null>(null);
  const [formAssignee, setFormAssignee] = useState<AdminUser | null>(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<HeadJudgeLogEntry | null>(null);

  // Fetch log entries with auto-refresh
  const {
    data: entries = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['head-judge-log', statusFilter === 'all' ? undefined : statusFilter],
    queryFn: () => headJudgeLogApi.list(statusFilter === 'all' ? undefined : statusFilter),
    refetchInterval: REFRESH_INTERVAL,
  });

  // Fetch counts for filter chips
  const { data: counts } = useQuery({
    queryKey: ['head-judge-log-counts'],
    queryFn: () => headJudgeLogApi.getCounts(),
    refetchInterval: REFRESH_INTERVAL,
  });

  // Fetch admin users for assignee dropdown
  const { data: adminUsers = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => headJudgeLogApi.getAdminUsers(),
  });

  // Fetch fields for field dropdown
  const { data: fields = [] } = useQuery({
    queryKey: ['fields'],
    queryFn: () => fieldsApi.list(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: HeadJudgeLogCreateRequest) => headJudgeLogApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['head-judge-log'] });
      queryClient.invalidateQueries({ queryKey: ['head-judge-log-counts'] });
      handleCloseDialog();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: HeadJudgeLogUpdateRequest }) =>
      headJudgeLogApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['head-judge-log'] });
      queryClient.invalidateQueries({ queryKey: ['head-judge-log-counts'] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => headJudgeLogApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['head-judge-log'] });
      queryClient.invalidateQueries({ queryKey: ['head-judge-log-counts'] });
      setDeleteTarget(null);
    },
  });

  // Sorted fields for dropdown (numeric fields first)
  const sortedFields = useMemo(() => {
    return [...fields].sort((a, b) => {
      const aNum = parseInt(a.name, 10);
      const bNum = parseInt(b.name, 10);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      if (!isNaN(aNum)) return -1;
      if (!isNaN(bNum)) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [fields]);

  // Handlers
  const handleOpenDialog = () => {
    setFormDescription('');
    setFormField(null);
    setFormAssignee(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormDescription('');
    setFormField(null);
    setFormAssignee(null);
  };

  const handleCreate = () => {
    if (!formDescription.trim()) return;
    createMutation.mutate({
      description: formDescription.trim(),
      field: formField?.name ?? null,
      assigned_to: formAssignee?.id ?? null,
    });
  };

  const handleStatusChange = (entry: HeadJudgeLogEntry, newStatus: HeadJudgeLogStatus) => {
    updateMutation.mutate({ id: entry.id, data: { status: newStatus } });
  };

  const handleAssigneeChange = (entry: HeadJudgeLogEntry, newAssigneeId: number | null) => {
    updateMutation.mutate({ id: entry.id, data: { assigned_to: newAssigneeId } });
  };

  const handleDescriptionChange = (entry: HeadJudgeLogEntry, newDescription: string) => {
    if (newDescription.trim() && newDescription.trim() !== entry.description) {
      updateMutation.mutate({ id: entry.id, data: { description: newDescription.trim() } });
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Head Judge Log
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => refetch()} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
            Add Entry
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load log entries
        </Alert>
      )}

      {/* Status Filter Chips */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`Kaikki (${counts?.total ?? 0})`}
            variant={statusFilter === 'all' ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter('all')}
            sx={{ fontWeight: statusFilter === 'all' ? 600 : 400 }}
          />
          {STATUS_OPTIONS.map((status) => {
            const config = STATUS_CONFIG[status];
            const count = counts?.[status] ?? 0;
            const isSelected = statusFilter === status;
            return (
              <Chip
                key={status}
                label={`${config.label} (${count})`}
                variant={isSelected ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter(status)}
                sx={{
                  fontWeight: isSelected ? 600 : 400,
                  bgcolor: isSelected ? config.bgColor : 'transparent',
                  color: isSelected ? config.color : 'inherit',
                  borderColor: config.bgColor,
                  '&:hover': {
                    bgcolor: isSelected ? config.bgColor : `${config.bgColor}20`,
                  },
                }}
              />
            );
          })}
        </Box>
      </Paper>

      {/* Log Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={80}>Aika</TableCell>
                <TableCell width={80}>Kenttä</TableCell>
                <TableCell>Mitä</TableCell>
                <TableCell width={140}>Tilanne</TableCell>
                <TableCell width={160}>Kuka teki</TableCell>
                <TableCell width={120}>Tekijä</TableCell>
                <TableCell width={60} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No entries found.
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => {
                  const statusConfig = STATUS_CONFIG[entry.status];
                  return (
                    <TableRow
                      key={entry.id}
                      sx={{
                        bgcolor: `${statusConfig.bgColor}30`,
                        '&:hover': { bgcolor: `${statusConfig.bgColor}40` },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2">{formatTime(entry.created_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        {entry.field_name && (
                          <Chip label={entry.field_name} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          defaultValue={entry.description}
                          onBlur={(e) => handleDescriptionChange(entry, e.target.value)}
                          fullWidth
                          multiline
                          maxRows={3}
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            sx: { fontSize: '0.875rem' },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={entry.status}
                            onChange={(e) => handleStatusChange(entry, e.target.value as HeadJudgeLogStatus)}
                            sx={{
                              bgcolor: statusConfig.bgColor,
                              color: statusConfig.color,
                              '& .MuiSelect-icon': { color: statusConfig.color },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                            }}
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <MenuItem key={status} value={status}>
                                {STATUS_CONFIG[status].label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={entry.assigned_to ?? ''}
                            onChange={(e) =>
                              handleAssigneeChange(entry, e.target.value ? Number(e.target.value) : null)
                            }
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>Ei valittu</em>
                            </MenuItem>
                            {adminUsers.map((user) => (
                              <MenuItem key={user.id} value={user.id}>
                                {user.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {entry.created_by_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(entry)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Entry Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Log Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Mitä"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              placeholder="Describe the issue or task..."
            />
            <Autocomplete
              options={sortedFields}
              getOptionLabel={(option) => option.name}
              value={formField}
              onChange={(_, newValue) => setFormField(newValue)}
              renderInput={(params) => <TextField {...params} label="Kenttä (optional)" />}
            />
            <Autocomplete
              options={adminUsers}
              getOptionLabel={(option) => option.name}
              value={formAssignee}
              onChange={(_, newValue) => setFormAssignee(newValue)}
              renderInput={(params) => <TextField {...params} label="Kuka teki (optional)" />}
            />
            {createMutation.isError && (
              <Alert severity="error">Failed to create entry. Please try again.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!formDescription.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Entry?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this entry?
          </Typography>
          {deleteTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              &ldquo;{deleteTarget.description.substring(0, 100)}
              {deleteTarget.description.length > 100 ? '...' : ''}&rdquo;
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
